import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Tag, Printer, Download, Trash2, Plus, ShoppingCart, CheckCircle2, RotateCcw, Droplets, Loader2, Scale } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { isOffline } from "@/lib/offline";
import { imprimirZPL, zplEtiqueta } from "@/lib/impresion";
import Barcode128 from "@/lib/barcode";
import { renderToStaticMarkup } from "react-dom/server";

/** Línea de presentación de la etiqueta: roll on / esenciero = "Esenciero 10 g" sin concentración. */
export function subEtiqueta(p) {
  const n = (p.nombre || "").toUpperCase();
  if (/ESENCIERO\s*4\s*(ML|G)\b/.test(n) || /esencia 4 g/i.test(p.presentacion || "")) return "Esencia 4 g";
  if (/ROLL\s*ON|ESENCIERO/.test(n) || /esenciero/i.test(p.presentacion || "")) return "Esenciero 10 g";
  return `${p.presentacion || ""}${p.concentracion ? ` · ${p.concentracion}` : ""}`;
}

/** Tamaño de letra del nombre según su largo: nombres largos (feromonas, etc.) bajan a 3 líneas con letra menor para salir completos. */
export function estiloNombre(nombre) {
  const n = (nombre || "").length;
  if (n <= 34) return { fontSize: "5.9pt", lineas: 2 };
  if (n <= 52) return { fontSize: "5.2pt", lineas: 3 };
  if (n <= 70) return { fontSize: "4.7pt", lineas: 3 };
  return { fontSize: "4.2pt", lineas: 3 };
}

/** Código de barras de venta a granel: GR-<SKU>-<gramos>. Al escanearlo el POS agrega el insumo con esa cantidad. */
export const codigoGranel = (sku, gramos) => `GR-${sku}-${String(gramos).replace(",", ".")}`;
export const parseGranel = (codigo) => { const m = /^GR-(.+)-(\d+(?:\.\d+)?)$/i.exec(String(codigo || "").trim()); return m ? { sku: m[1].toUpperCase(), gramos: parseFloat(m[2]) } : null; };

function EtiquetaZebra({ product, mostrarPrecio, testId }) {
  const est = estiloNombre(product.nombre);
  return (
    <div
      className="etiqueta-item bg-white text-black flex flex-col shrink-0 border border-brand-line print:border-0"
      style={{ width: "40mm", height: "13mm", padding: "0.6mm 1.2mm", boxSizing: "border-box", fontFamily: "'Montserrat', sans-serif", breakAfter: "page", pageBreakAfter: "always" }}
      data-testid={testId}
    >
      <div className="flex items-start" style={{ height: "7.6mm", gap: "0.8mm" }}>
        <img src="/rubi-diamante.png" alt="" className="print:hidden" style={{ height: "2.8mm", width: "auto", marginTop: "0.25mm" }} />
        <img src="/rubi-diamante-negro.png" alt="" className="hidden print:block" style={{ height: "2.8mm", width: "auto", marginTop: "0.25mm" }} />
        <div className="flex-1 min-w-0">
          <div className="font-bold uppercase" style={{ fontSize: est.fontSize, lineHeight: 1.02, letterSpacing: 0, display: "-webkit-box", WebkitLineClamp: est.lineas, WebkitBoxOrient: "vertical", overflow: "hidden", wordBreak: "break-word" }} data-testid={`${testId}-nombre`}>{product.nombre}</div>
          <div className="truncate" style={{ fontSize: "5pt", lineHeight: 1, marginTop: "0.3mm", color: "#000", fontWeight: 600 }}>
            {product.granel ? <b>{product.granel.gramos} g a granel</b> : subEtiqueta(product)} · <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{product.sku}</span>
          </div>
        </div>
        {mostrarPrecio && <div className="font-extrabold shrink-0" style={{ fontSize: "6pt" }}>${(product.granel ? product.granel.gramos * (product.precio_publico || 0) : product.precios?.propia || 0).toFixed(0)}</div>}
      </div>
      <div style={{ height: "3.4mm", marginTop: "0.3mm", padding: "0 1.5mm" }}>
        <Barcode128 value={product.codigo_barras || product.sku} height={10} className="w-full h-full block" />
      </div>
    </div>
  );
}

// Impresión desde navegador: documento limpio (sin la app) con una página 40×13 mm por etiqueta
function htmlEtiquetas(lote, mostrarPrecio, etiquetasDe) {
  const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const una = (p) => { const est = estiloNombre(p.nombre); return `<div class="et"><div class="top"><img src="${window.location.origin}/rubi-diamante-negro.png" alt=""><div class="txt"><div class="nombre" style="font-size:${est.fontSize};-webkit-line-clamp:${est.lineas}">${esc(p.nombre)}</div><div class="sub">${p.granel ? `<b>${esc(p.granel.gramos)} g a granel</b>` : esc(subEtiqueta(p))} · <span class="mono">${esc(p.sku)}</span></div></div>${mostrarPrecio ? `<div class="precio">$${(p.granel ? p.granel.gramos * (p.precio_publico || 0) : p.precios?.propia || 0).toFixed(0)}</div>` : ""}</div><div class="bc">${renderToStaticMarkup(<Barcode128 value={p.codigo_barras || p.sku} height={10} />)}</div></div>`; };
  const cuerpo = lote.flatMap((x) => Array.from({ length: etiquetasDe(x) }, () => una(x.product))).join("");
  return `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Etiquetas RubiPOS</title><link rel="preconnect" href="https://fonts.googleapis.com"><link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@500;700&display=swap" rel="stylesheet">
<style>@page{size:40mm 13mm;margin:0}html,body{margin:0;padding:0;background:#fff}body{font-family:Montserrat,Arial,sans-serif;color:#000;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.et{width:40mm;height:13mm;box-sizing:border-box;padding:.6mm 1.2mm;display:flex;flex-direction:column;overflow:hidden;page-break-after:always;break-after:page}
.top{display:flex;align-items:flex-start;gap:.8mm;height:7.6mm}.top img{height:2.8mm;width:auto;margin-top:.25mm}.txt{flex:1;min-width:0}
.nombre{font-weight:700;text-transform:uppercase;font-size:5.9pt;line-height:1.02;letter-spacing:0;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;word-break:break-word}
.sub{font-size:5pt;line-height:1;margin-top:.3mm;color:#000;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.mono{font-family:"JetBrains Mono",Consolas,monospace}.precio{font-weight:800;font-size:6pt;flex-shrink:0}
.bc{height:3.4mm;margin-top:.3mm;padding:0 1.5mm}.bc svg{width:100%;height:100%;display:block}</style></head><body>${cuerpo}
<script>window.onload=function(){setTimeout(function(){window.print()},250)}</script></body></html>`;
}

export default function Etiquetas({ modal = false, onPasarVenta }) {
  const navigate = useNavigate();
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const cacheRef = useRef(new Map());
  const [q, setQ] = useState("");
  const [lote, setLote] = useState([]);
  const [mostrarPrecio, setMostrarPrecio] = useState(false);
  const [impreso, setImpreso] = useState(false);
  const [insumos, setInsumos] = useState([]);
  const [granel, setGranel] = useState(null); // { insumo, gramos }

  // Insumos/esencias vendibles a granel (se buscan por SKU o nombre junto con los PACKs)
  useEffect(() => { api.get("/componentes").then((r) => setInsumos((r.data || []).filter((c) => c.sku && (c.precio_publico || 0) > 0))).catch(() => {}); }, []);
  const insumosRes = q.trim().length >= 2 ? insumos.filter((c) => { const t = q.trim().toLowerCase(); return c.sku.toLowerCase().includes(t) || (c.nombre || "").toLowerCase().includes(t); }).slice(0, 4) : [];

  function abrirGranel(c) { setGranel({ insumo: c, gramos: "" }); setQ(""); }
  function agregarGranel() {
    const g = Math.round((parseFloat(granel.gramos) || 0) * 100) / 100;
    if (!(g > 0)) return toast.error("Indica los gramos a vender");
    const c = granel.insumo;
    const product = { id: `granel-${c.id}-${g}`, componente_id: c.id, sku: c.sku, nombre: c.nombre, presentacion: `${g} g a granel`, concentracion: null, tipo_item: "insumo", unidad: c.unidad, precio_publico: c.precio_publico, precio_mayoreo: c.precio_mayoreo, codigo_barras: codigoGranel(c.sku, g), etiquetas_por_pieza: 1, precios: { propia: g * (c.precio_publico || 0) }, granel: { gramos: g } };
    sumar(product, 1);
    setGranel(null);
    toast.success(`${c.nombre} · ${g} g a granel · ${mxnG(g * (c.precio_publico || 0))}`);
  }
  const mxnG = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`;

  // Búsqueda en el servidor (catálogo de +20k PACKs) con caché local para operar sin conexión
  useEffect(() => {
    const term = q.trim().toLowerCase();
    if (term.length < 2) { setResultados([]); setBuscando(false); return undefined; }
    const local = () => [...cacheRef.current.values()].filter((p) => p.nombre.toLowerCase().includes(term) || p.sku.toLowerCase().includes(term) || (p.codigo_barras || "").toLowerCase().includes(term)).slice(0, 8);
    if (isOffline()) { setResultados(local()); return undefined; }
    setBuscando(true);
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/productos/buscar", { params: { q: term } }); data.forEach((p) => cacheRef.current.set(p.id, p)); setResultados(data.slice(0, 8)); } catch { setResultados(local()); } finally { setBuscando(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const etiquetasDe = (x) => x.piezas * (x.product.etiquetas_por_pieza || 1);
  const totalEtiquetas = lote.reduce((s, x) => s + etiquetasDe(x), 0);
  const totalPiezas = lote.reduce((s, x) => s + x.piezas, 0);

  function sumar(product, n) {
    setImpreso(false);
    setLote((prev) => {
      const i = prev.findIndex((x) => x.product.id === product.id);
      if (i >= 0) {
        const c = [...prev];
        c[i] = { ...c[i], piezas: c[i].piezas + n };
        return c;
      }
      return [...prev, { product, piezas: n }];
    });
  }

  function agregar(p) {
    sumar(p, 1);
    setQ("");
    toast.success(`${p.nombre} · ${p.etiquetas_por_pieza} etiqueta${p.etiquetas_por_pieza > 1 ? "s" : ""} por pieza`);
  }

  function setPiezas(id, val) {
    setImpreso(false);
    setLote((prev) => prev.map((y) => (y.product.id === id ? { ...y, piezas: Math.max(1, parseInt(val) || 1) } : y)));
  }

  async function imprimir() {
    if (!lote.length) return;
    const zpl = lote.flatMap((x) => Array.from({ length: etiquetasDe(x) }, () => zplEtiqueta(x.product))).join("");
    const modo = await imprimirZPL(zpl, () => {
      const w = window.open("", "_blank", "width=520,height=720");
      if (!w) return toast.error("Permite las ventanas emergentes para imprimir las etiquetas");
      w.document.write(htmlEtiquetas(lote, mostrarPrecio, etiquetasDe));
      w.document.close();
    });
    setImpreso(true);
    toast.success(`${totalEtiquetas} etiqueta(s) ${modo === "agente" ? "enviadas directo a la Zebra" : "enviadas a la impresora predeterminada"}`);
  }

  function pasarAVenta() {
    const items = lote.map((x) => (x.product.granel ? { product: { ...x.product, id: x.product.componente_id }, cantidad: x.product.granel.gramos * x.piezas } : { product: x.product, cantidad: x.piezas }));
    if (modal && onPasarVenta) return onPasarVenta(items);
    sessionStorage.setItem("rubipos_precarga", JSON.stringify(items));
    navigate("/");
  }

  async function descargarDB() {
    let productos;
    try { ({ data: productos } = await api.get("/productos", { params: { limit: 5000 } })); } catch { return toast.error("No se pudo descargar la base de etiquetas"); }
    productos.forEach((p) => cacheRef.current.set(p.id, p));
    const data = productos.map((p) => ({ sku: p.sku, nombre: p.nombre, codigo_barras: p.codigo_barras, presentacion: p.presentacion, concentracion: p.concentracion, etiquetas_por_pieza: p.etiquetas_por_pieza, precios: p.precios }));
    const blob = new Blob([JSON.stringify({ generado: new Date().toISOString(), productos: data }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rubipos-etiquetas-db-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Base de etiquetas descargada (${productos.length} PACKs, operación sin internet)`);
  }

  return (
    <div className={modal ? "animate-fade-up" : "p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto animate-fade-up"} data-testid="etiquetas-page">
      <style>{`@media print { @page { size: 40mm 13mm; margin: 0; } .labels-flow { display: block !important; gap: 0 !important; } }`}</style>

      <div className={`flex items-start justify-between flex-wrap gap-4 ${modal ? "mb-4" : "mb-8"}`}>
        <div>
          <div className="eyebrow mb-1">Zebra · 40 × 13 mm</div>
          {!modal && <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-brand-ink">Impresión de etiquetas</h1>}
          {!modal && <p className="text-sm text-brand-muted mt-1 max-w-xl">Elige el SKU y RubiPOS calcula las etiquetas por pieza según la presentación. Al escanear la etiqueta impresa, el artículo entra al carrito.</p>}
        </div>
        <div className="flex gap-2 no-print">
          <button data-testid="download-labels-db" onClick={descargarDB} className="btn-soft h-11 px-4 text-sm"><Download className="w-4 h-4" /> Base offline</button>
          <button data-testid="label-print-button" onClick={imprimir} disabled={!lote.length} className="btn-amber h-11 px-5 text-sm">
            <Printer className="w-4 h-4" /> Imprimir {totalEtiquetas > 0 ? `${totalEtiquetas} etiqueta${totalEtiquetas !== 1 ? "s" : ""}` : ""}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6 lg:gap-8">
        <div className="col-span-12 lg:col-span-5 no-print">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3.5 top-3.5 text-brand-muted" />
            <input
              data-testid="label-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && resultados[0]) agregar(resultados[0]); }}
              placeholder="Buscar por SKU, nombre o código…"
              className="field h-12 pl-10 pr-10"
              autoFocus
            />
            {buscando && <Loader2 className="w-4 h-4 absolute right-3.5 top-4 animate-spin text-brand-muted" />}
          </div>
          <div className="mt-2 space-y-1.5" data-testid="label-search-results">
            {insumosRes.map((c) => (
              <button key={`ins-${c.id}`} data-testid={`label-granel-${c.sku}`} onClick={() => abrirGranel(c)} className="w-full flex items-center gap-3 surface-card p-2.5 border-brand-amber-line hover:border-brand-amber hover:-translate-y-px transition-[border-color,transform] duration-150 text-left animate-pop">
                <div className="w-10 h-10 rounded-xl bg-brand-amber-soft overflow-hidden shrink-0 flex items-center justify-center"><Scale className="w-4 h-4 text-brand-amber-dark" /></div>
                <div className="flex-1 min-w-0"><div className="text-[11px] leading-tight font-semibold text-brand-ink break-words">{c.nombre}</div><div className="font-mono text-[10px] uppercase text-brand-muted">{c.sku} · {mxnG(c.precio_publico)}/{c.unidad}</div></div>
                <span className="chip chip-amber">Venta a granel</span>
                <Plus className="w-4 h-4 text-brand-amber-dark shrink-0" />
              </button>
            ))}
            {resultados.map((p) => (
              <button
                key={p.id}
                data-testid={`label-result-${p.sku}`}
                onClick={() => agregar(p)}
                className="w-full flex items-center gap-3 surface-card p-2.5 hover:border-brand-amber hover:-translate-y-px transition-[border-color,transform] duration-150 text-left animate-pop"
              >
                <div className="w-10 h-10 rounded-xl bg-brand-surface overflow-hidden shrink-0 flex items-center justify-center">
                  {p.foto_url ? <img src={p.foto_url} alt="" className="w-full h-full object-cover" loading="lazy" /> : <Droplets className="w-4 h-4 text-brand-muted" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] leading-tight font-semibold text-brand-ink break-words" data-testid={`etiqueta-result-nombre-${p.sku}`}>{p.nombre}</div>
                  <div className="font-mono text-[10px] uppercase text-brand-muted">{p.sku} · {p.presentacion}</div>
                </div>
                <span className="chip chip-amber">{p.etiquetas_por_pieza} etiq./pza</span>
                <Plus className="w-4 h-4 text-brand-amber-dark shrink-0" />
              </button>
            ))}
            {q.trim().length >= 2 && !buscando && resultados.length === 0 && insumosRes.length === 0 && <p className="text-sm text-brand-muted py-4 text-center">Sin resultados para “{q}”.</p>}
            {q.trim().length === 1 && <p className="text-xs text-brand-muted py-2 text-center">Escribe al menos 2 caracteres.</p>}
            {resultados.length === 0 && (
              <div className="hidden md:flex flex-col items-center justify-center pt-6 pb-2 select-none pointer-events-none" data-testid="etiquetas-mascota">
                <img src="/rubi-mascota-impresora.png" alt="" className="mascot h-40 lg:h-52 w-auto drop-shadow-lg" draggable={false} />
                <p className="text-[11px] text-brand-muted mt-2 text-center max-w-[240px]">Busca el SKU, arma el lote y Rubi lo manda a la Zebra. Luego “Pasar a venta” sin volver a escanear.</p>
              </div>
            )}
          </div>

          {lote.length > 0 && (
            <div className="mt-6 surface-card p-4 animate-pop" data-testid="label-batch-list">
              <div className="flex items-center justify-between mb-3">
                <div className="eyebrow">Lote de impresión</div>
                <div className="text-xs font-semibold text-brand-graphite" data-testid="batch-summary">
                  <span className="font-mono">{totalPiezas}</span> pza{totalPiezas !== 1 ? "s" : ""} · <span className="font-mono text-brand-amber-dark">{totalEtiquetas}</span> etiq.
                </div>
              </div>
              {lote.map((x) => (
                <div key={x.product.id} className="py-2.5 border-b border-brand-line/70 last:border-0" data-testid={`batch-row-${x.product.sku}`}>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] leading-tight font-semibold text-brand-ink break-words" data-testid={`etiqueta-cola-nombre-${x.product.sku}`}>{x.product.nombre}</div>
                      <div className="font-mono text-[10px] uppercase text-brand-muted truncate">{x.product.sku} · {x.product.presentacion} · {x.product.etiquetas_por_pieza} etiq./pza</div>
                    </div>
                    <div className="flex gap-1">
                      {[1, 2].map((n) => (
                        <button key={n} data-testid={`batch-add-${x.product.sku}-${n}`} onClick={() => sumar(x.product, n)} className="h-8 px-2.5 rounded-lg text-xs font-bold bg-brand-surface text-brand-graphite hover:bg-brand-amber hover:text-brand-ink active:scale-95 transition-[background-color,color,transform] duration-100">
                          +{n}
                        </button>
                      ))}
                    </div>
                    <input
                      data-testid={`batch-qty-input-${x.product.sku}`}
                      type="number" min="1" value={x.piezas}
                      onChange={(e) => setPiezas(x.product.id, e.target.value)}
                      className="field w-16 h-8 px-2 font-mono text-xs text-center"
                      title="Piezas"
                    />
                    <span className="chip chip-ink font-mono w-16 justify-center" data-testid={`batch-labels-${x.product.sku}`}>{etiquetasDe(x)} etiq.</span>
                    <button data-testid={`batch-remove-${x.product.sku}`} onClick={() => setLote(lote.filter((y) => y.product.id !== x.product.id))} className="p-1.5 text-brand-muted hover:text-rose-600 transition-colors duration-100">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-brand-line/70">
                <label className="flex items-center gap-2 text-xs font-semibold text-brand-graphite cursor-pointer select-none">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={mostrarPrecio}
                    data-testid="label-price-toggle"
                    onClick={() => setMostrarPrecio(!mostrarPrecio)}
                    className={`relative w-9 h-5 rounded-full transition-colors duration-150 ${mostrarPrecio ? "bg-brand-amber" : "bg-brand-line"}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-150 ${mostrarPrecio ? "translate-x-4" : "translate-x-0.5"}`} />
                  </button>
                  Mostrar precio en etiqueta
                </label>
                <button data-testid="label-clear-batch" onClick={() => { setLote([]); setImpreso(false); }} className="text-xs font-semibold text-brand-muted hover:text-rose-600 flex items-center gap-1 transition-colors duration-100">
                  <RotateCcw className="w-3.5 h-3.5" /> Nuevo lote
                </button>
              </div>
            </div>
          )}

          {(impreso || modal) && lote.length > 0 && (
            <div className="mt-4 rounded-2xl bg-brand-ink text-white p-5 animate-pop" data-testid="printed-banner">
              <div className="flex items-center gap-2 text-brand-amber font-display font-bold">
                {impreso ? <><CheckCircle2 className="w-5 h-5" /> Lote enviado a impresión</> : <><ShoppingCart className="w-5 h-5" /> Lote listo</>}
              </div>
              <p className="text-sm text-white/60 mt-1">Pasa estas {totalPiezas} pieza{totalPiezas !== 1 ? "s" : ""} directo al carrito sin volver a escanear.</p>
              <button data-testid="pass-to-pos-cart-button" onClick={pasarAVenta} className="btn-amber w-full h-11 mt-4">
                <ShoppingCart className="w-4 h-4" /> Pasar a venta ({totalPiezas})
              </button>
            </div>
          )}
        </div>

        <div className="col-span-12 lg:col-span-7">
          <div className="eyebrow mb-2 flex items-center gap-1.5 no-print">
            <Tag className="w-3.5 h-3.5" /> Vista previa · tamaño real 40 × 13 mm (ampliada ×2.2)
          </div>
          <div className="print-area bg-brand-surface rounded-2xl border border-brand-line p-6 min-h-[320px] overflow-x-auto" data-testid="labels-preview">
            {lote.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-brand-muted no-print">
                <Tag className="w-10 h-10 mb-3 text-brand-line" />
                <p className="text-sm">Agrega SKUs al lote para previsualizar las etiquetas.</p>
              </div>
            )}
            <div className="labels-flow label-zoom flex flex-col gap-2 items-start">
              {lote.flatMap((x) =>
                Array.from({ length: Math.min(etiquetasDe(x), 12) }).map((_, i) => (
                  <EtiquetaZebra key={`${x.product.id}-${i}`} product={x.product} mostrarPrecio={mostrarPrecio} testId={`label-preview-${x.product.sku}-${i}`} />
                ))
              )}
            </div>
            {lote.some((x) => etiquetasDe(x) > 12) && <p className="text-[10px] text-brand-muted mt-2 no-print">Vista previa limitada a 12 por artículo; la impresión incluye todas.</p>}
          </div>
          <p className="text-xs text-brand-muted mt-3 no-print">Consejo: define la Zebra como impresora predeterminada y, en el diálogo de Chrome, desactiva “Encabezados y pies de página” con tamaño de papel 40 × 13 mm.</p>
          {isOffline() && <p className="text-xs text-brand-amber-dark mt-1 no-print">Modo offline: las etiquetas se generan localmente sin internet.</p>}
        </div>
      </div>
      <Dialog open={!!granel} onOpenChange={(o) => !o && setGranel(null)}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="granel-dialog">
          <DialogHeader><div className="eyebrow">Venta a granel</div><DialogTitle className="font-display text-lg leading-tight">{granel?.insumo?.nombre}</DialogTitle></DialogHeader>
          {granel && (
            <form onSubmit={(e) => { e.preventDefault(); agregarGranel(); }} className="space-y-3">
              <p className="text-xs text-brand-muted">{granel.insumo.sku} · {mxnG(granel.insumo.precio_publico)} por {granel.insumo.unidad}. Captura los gramos que se venden: la etiqueta sale con esa cantidad y al escanearla en caja se agrega con ese peso.</p>
              <div className="flex items-center gap-2"><input autoFocus data-testid="granel-gramos" type="number" min="0.01" step="0.01" inputMode="decimal" value={granel.gramos} onChange={(e) => setGranel({ ...granel, gramos: e.target.value })} placeholder="Gramos" className="field h-12 font-mono text-xl text-right flex-1" /><span className="text-sm font-bold text-brand-ink">{granel.insumo.unidad}</span></div>
              <div className="flex flex-wrap gap-1.5">{[50, 100, 250, 500, 1000].map((g) => <button type="button" key={g} data-testid={`granel-rapido-${g}`} onClick={() => setGranel({ ...granel, gramos: String(g) })} className="chip chip-ink hover:bg-brand-amber hover:text-brand-ink transition-colors">{g} g</button>)}</div>
              <div className="rounded-xl bg-brand-surface px-3 py-2 text-sm flex items-center justify-between"><span className="text-brand-muted">Precio público</span><b className="font-mono" data-testid="granel-precio">{mxnG((parseFloat(granel.gramos) || 0) * (granel.insumo.precio_publico || 0))}</b></div>
              <div className="font-mono text-[11px] text-brand-muted">Código: {codigoGranel(granel.insumo.sku, parseFloat(granel.gramos) || 0)}</div>
              <button type="submit" data-testid="granel-agregar" className="btn-amber w-full h-11"><Scale className="w-4 h-4" /> Agregar etiqueta a granel</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
